import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import {
  employeeService,
  departmentService,
  reportingManagerService,
} from "../services/apiService.jsx";
import "./Dashboard.css";

const UpdateEmployee = () => {
  const { id } = useParams(); // Get employee ID from URL
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [departments, setDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [employeeData, setEmployeeData] = useState({
    employee_id: "",
    first_name: "",
    last_name: "",
    Designation: "",
    employee_code: "",
    email_id: "",
    Contact_Number: "",
    birthday: "",
    location: "",
    department_name: "",
    reporting_manager_id: "",
    Joiningdate: "",
    status: "",
    profile_photo: null,
    Username: "",
    role: "",
    existing_photo: "",
  });

  // Create refs for date inputs
  const dobRef = useRef(null);
  const joiningDateRef = useRef(null);

  // Fetch employee details, departments, and reporting managers on component mount
  useEffect(() => {
    if (id) {
      fetchEmployeeDetails();
      fetchFormData();
    }
  }, [id]);

  const fetchEmployeeDetails = async () => {
    setLoading(true);
    try {
      const response = await employeeService.getEmployeeDetails(id);
      console.log("Employee Details Response:", response);

      // Handle different response structures
      let empData = null;

      if (response?.data?.data) {
        // If response has nested data property
        empData = response.data.data;
      } else if (response?.data) {
        // If response has data property directly
        empData = response.data;
      } else if (response?.success && response?.data) {
        // If response has success and data properties
        empData = response.data;
      }

      if (empData) {
        // Format dates for input fields (YYYY-MM-DD)
        const formatDate = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          // Check if date is valid
          if (isNaN(date.getTime())) return "";
          return date.toISOString().split("T")[0];
        };

        setEmployeeData({
          employee_id: empData.employee_id || "",
          first_name: empData.first_name || "",
          last_name: empData.last_name || "",
          Designation: empData.Designation || "",
          employee_code: empData.employee_code || "",
          email_id: empData.email_id || "",
          Contact_Number: empData.Contact_Number || "",
          birthday: formatDate(empData.birthday),
          location: empData.location || "",
          department_name: empData.department_name || "",
          reporting_manager_id: empData.reporting_manager_id || "",
          Joiningdate: formatDate(empData.Joiningdate),
          status: empData.status || "",
          profile_photo: null,
          Username: empData.Username || "",
          role: empData.role || "",
          existing_photo: empData.profile_photo || "",
        });
      } else {
        setError("Failed to load employee details. No data found.");
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setError("Failed to load employee details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      // Fetch departments and reporting managers in parallel
      const [deptsResponse, managersResponse] = await Promise.all([
        departmentService.getAllDepartments(),
        reportingManagerService.getAllReportingManagers(),
      ]);

      console.log("Departments Response:", deptsResponse);
      console.log("Managers Response:", managersResponse);

      // Extract departments data - handle different response structures
      if (deptsResponse?.data?.data) {
        setDepartments(deptsResponse.data.data);
      } else if (deptsResponse?.data) {
        setDepartments(deptsResponse.data);
      } else if (deptsResponse?.success && deptsResponse?.data) {
        setDepartments(deptsResponse.data);
      }

      // Extract reporting managers data - handle different response structures
      if (managersResponse?.data?.data) {
        setReportingManagers(managersResponse.data.data);
      } else if (managersResponse?.data) {
        setReportingManagers(managersResponse.data);
      } else if (managersResponse?.success && managersResponse?.data) {
        setReportingManagers(managersResponse.data);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      // Don't show error for form data, just log it
    }
  };

  // Function to handle date input click - opens the date picker
  const handleDateClick = (ref) => {
    if (ref.current) {
      ref.current.showPicker();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setEmployeeData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setEmployeeData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError("");
    setSuccess("");
    setValidationErrors({});

    // Clear all previous error messages
    document.querySelectorAll(".text-danger").forEach((el) => {
      el.style.display = "none";
    });

    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Map fields to match backend expectations (similar to CreateEmployee)
      formData.append("employee_code", employeeData.employee_code);
      formData.append("username", employeeData.Username);

      // Only append password if it's provided
      if (employeeData.password) {
        formData.append("password", employeeData.password);
      }

      formData.append("phone", employeeData.Contact_Number);
      formData.append("designation", employeeData.Designation);
      formData.append("first_name", employeeData.first_name);
      formData.append("last_name", employeeData.last_name);
      formData.append("email_id", employeeData.email_id);
      formData.append("birthday", employeeData.birthday);
      formData.append("location", employeeData.location);
      formData.append("department_name", employeeData.department_name);
      formData.append(
        "reporting_manager_id",
        employeeData.reporting_manager_id,
      );
      formData.append("joining_date", employeeData.Joiningdate);
      formData.append("status", employeeData.status);
      formData.append("role", employeeData.role);

      // Append file if a new one is selected
      if (employeeData.profile_photo) {
        formData.append("profile_photo", employeeData.profile_photo);
      }

      // Log FormData entries for debugging
      console.log("Sending update data to server:");
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": " + pair[1]);
      }

      // Make the API call to update employee
      const response = await employeeService.updateEmployee(id, formData);

      console.log("Update Employee Response:", response);

      setSuccess("Employee updated successfully!");

      // Redirect to employees list after 2 seconds
      setTimeout(() => {
        navigate("/employees");
      }, 2000);
    } catch (error) {
      console.error("Error updating employee:", error);

      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;

          // Handle validation errors array from backend
          if (errorData.errors && Array.isArray(errorData.errors)) {
            const fieldErrors = {};
            errorData.errors.forEach((err) => {
              fieldErrors[err.field] = err.message;

              // Show error in the UI
              const errorElement = document.getElementById(`${err.field}Error`);
              if (errorElement) {
                errorElement.style.display = "block";
                errorElement.textContent = err.message;
              }
            });
            setValidationErrors(fieldErrors);
          }
          // Handle if errors is an object
          else if (errorData.errors && typeof errorData.errors === "object") {
            Object.keys(errorData.errors).forEach((field) => {
              const errorElement = document.getElementById(`${field}Error`);
              if (errorElement) {
                errorElement.style.display = "block";
                errorElement.textContent = errorData.errors[field];
              }
            });
          } else {
            setError(
              errorData.message ||
                "Validation error. Please check your inputs.",
            );
          }
        } else if (error.response.status === 401) {
          setError("You are not authorized. Please login again.");
        } else if (error.response.status === 403) {
          setError("You don't have permission to update employees.");
        } else if (error.response.status === 404) {
          setError("Employee not found.");
        } else {
          setError(
            error.response.data?.message ||
              "Failed to update employee. Please try again.",
          );
        }
      } else if (error.request) {
        setError(
          "No response from server. Please check your internet connection.",
        );
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1>Update Employee</h1>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading employee details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header with back button beside title */}
      <div className="d-flex align-items-center" style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => navigate("/employees")}
          className="btn btn-link text-dark p-0 me-2"
          style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
          title="Go back to employees list"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Update Employee</h1>
      </div>

      {/* Subtitle */}
      <p className="text-muted" style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
        Updating employee: {employeeData.first_name} {employeeData.last_name} (ID: {id})
      </p>

      {/* Success/Error Messages */}
      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}

      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card" style={{ marginTop: 0, border: '1px solid #dee2e6' }}>
              <div className="card-body" style={{ padding: '1rem' }}>
                <form
                  className="row g-2"
                  id="employeeForm"
                  noValidate
                  encType="multipart/form-data"
                  method="post"
                  onSubmit={handleSubmit}
                >
                  <div className="col-md-3">
                    <label htmlFor="inputFirstName" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      className="form-control form-control-sm"
                      id="inputFirstName"
                      placeholder="Enter first name"
                      value={employeeData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="first_nameError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      First name is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputLastName" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      className="form-control form-control-sm"
                      id="inputLastName"
                      placeholder="Enter last name"
                      value={employeeData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="last_nameError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Last name is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputDesignation" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Designation *
                    </label>
                    <select
                      name="Designation"
                      id="inputDesignation"
                      className="form-select form-select-sm"
                      value={employeeData.Designation}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Choose...</option>
                      <option>Manager</option>
                      <option>Software Developer</option>
                      <option>Operation</option>
                      <option>Data Analyst</option>
                      <option>Service Support</option>
                      <option>Support</option>
                      <option>Project Manager</option>
                      <option>Intern</option>
                      <option>Management Trainee</option>
                      <option>Project Engineer</option>
                      <option>E-Surveillance Executive</option>
                      <option>Office Boy</option>
                      <option>Asst. Manager</option>
                    </select>
                    <small
                      id="designationError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Designation is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputEmployeeID" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Employee Code *
                    </label>
                    <input
                      type="text"
                      name="employee_code"
                      className="form-control form-control-sm"
                      id="inputEmployeeID"
                      placeholder="Enter Employee Code"
                      value={employeeData.employee_code}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="employee_codeError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Employee code is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputEmail" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Email ID *
                    </label>
                    <input
                      type="email"
                      name="email_id"
                      className="form-control form-control-sm"
                      id="inputEmail"
                      placeholder="Enter a valid email"
                      value={employeeData.email_id}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="email_idError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Email ID is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputMobileNumber" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="Contact_Number"
                      className="form-control form-control-sm"
                      id="inputMobileNumber"
                      pattern="[0-9]{10}"
                      maxLength="10"
                      placeholder="Enter 10-digit mobile number"
                      value={employeeData.Contact_Number}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="phoneError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Phone number is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputDateOfBirth" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Date of Birth
                    </label>
                    <div
                      onClick={() => handleDateClick(dobRef)}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="date"
                        name="birthday"
                        className="form-control form-control-sm"
                        id="inputDateOfBirth"
                        value={employeeData.birthday}
                        onChange={handleInputChange}
                        ref={dobRef}
                      />
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputLocation" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      className="form-control form-control-sm"
                      id="inputLocation"
                      placeholder="Enter location"
                      value={employeeData.location}
                      onChange={handleInputChange}
                      required
                    />
                    <div id="locationError" className="text-danger" style={{ fontSize: '0.75rem' }}></div>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputDepartment" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Department *
                    </label>
                    <select
                      name="department_name"
                      className="form-control form-control-sm"
                      id="inputDepartment"
                      value={employeeData.department_name}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.length > 0 ? (
                        departments.map((dept, index) => (
                          <option key={index} value={dept}>
                            {dept}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          Loading departments...
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label
                      htmlFor="inputReportingManager"
                      className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}
                    >
                      Reporting Manager *
                    </label>
                    <select
                      name="reporting_manager_id"
                      className="form-control form-control-sm"
                      id="inputReportingManager"
                      value={employeeData.reporting_manager_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Reporting Manager</option>
                      {reportingManagers.length > 0 ? (
                        reportingManagers.map((manager, index) => (
                          <option key={index} value={manager.employee_id}>
                            {manager.name} - {manager.role || 'Manager'}{manager.department_name ? ` - ${manager.department_name}` : ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          Loading managers...
                        </option>
                      )}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputJoiningDate" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Joining Date *
                    </label>
                    <div
                      onClick={() => handleDateClick(joiningDateRef)}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="date"
                        name="Joiningdate"
                        className="form-control form-control-sm"
                        id="inputJoiningDate"
                        value={employeeData.Joiningdate}
                        onChange={handleInputChange}
                        required
                        ref={joiningDateRef}
                      />
                    </div>
                    <small
                      id="joining_dateError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Joining date is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputStatus" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Status *
                    </label>
                    <select
                      name="status"
                      id="inputStatus"
                      className="form-select form-select-sm"
                      value={employeeData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Choose...</option>
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputPhoto" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Profile Photo
                    </label>
                    {employeeData.existing_photo && (
                      <div className="mb-1">
                        <img
                          src={employeeData.existing_photo}
                          alt="Current"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                          }}
                          className="img-thumbnail"
                        />
                        <small className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>Current</small>
                      </div>
                    )}
                    <input
                      name="profile_photo"
                      type="file"
                      className="form-control form-control-sm"
                      id="inputPhoto"
                      accept="image/*"
                      onChange={handleInputChange}
                    />
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Leave empty to keep current photo
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputUsername" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      name="Username"
                      className="form-control form-control-sm"
                      id="inputusername"
                      value={employeeData.Username}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      id="usernameError"
                      className="text-danger"
                      style={{ display: "none", fontSize: '0.75rem' }}
                    >
                      Username is required
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputPassword" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      className="form-control form-control-sm"
                      id="inputPassword"
                      placeholder="Leave empty to keep current password"
                      onChange={handleInputChange}
                      minLength="8"
                    />
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Leave empty to keep current password
                    </small>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputRole" className="form-label" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Role *
                    </label>
                    <select
                      name="role"
                      id="inputRole"
                      className="form-select form-select-sm"
                      value={employeeData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Choose...</option>
                      <option>admin</option>
                      <option>HOD</option>
                      <option>team lead</option>
                      <option>employee</option>
                    </select>
                  </div>

                  <div className="col-12 d-flex justify-content-end gap-2" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate("/employees")}
                      style={{ padding: '0.25rem 1rem' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={isSubmitting}
                      style={{ padding: '0.25rem 1rem' }}
                    >
                      {isSubmitting ? "Updating..." : "Update Employee"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default UpdateEmployee;
